import type { Dispatch, SetStateAction } from 'react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import type { GetPaymentInfoResponse } from '@jotta/grpc-connect-openapi/payment'
import type { BrandThemeIcon } from '@jotta/types/Brand'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../Accordion/Accordion'
import SvgBoldCheck from '../../generated/icons/SvgBoldCheck'
import SvgMastercard from '../../generated/icons/SvgMastercard'
import SvgPaypal from '../../generated/icons/SvgPaypal'
import SvgVippsText from '../../generated/icons/SvgVippsText'
import SvgVisa from '../../generated/icons/SvgVisa'
import { PlainBrandIcon } from '../../Icons/BrandIcon/BrandIcon'
import { Divider } from '../Divider/Divider'

import { PaymentMethodOption } from './paymentMethodOptions'

type PaymentAccordionItem = {
  label: string
  sublabel?: string
  icons: BrandThemeIcon[]
  method: PaymentMethodOption
}

export function PaymentAccordion({
  paymentMethods,
  setUseCurrent,
  currentPaymentInfo,
  setPaymentOption,
}: {
  paymentMethods: PaymentMethodOption[] | undefined
  setUseCurrent: (arg: boolean) => void
  currentPaymentInfo?: GetPaymentInfoResponse | undefined
  setPaymentOption: Dispatch<SetStateAction<PaymentMethodOption | undefined>>
}) {
  const currentPaymentMethod = currentPaymentInfo?.paymentInfo?.paymentMethod
  const stripeSupported = paymentMethods?.some(opt => {
    switch (opt) {
      case PaymentMethodOption.STRIPE_CARD:
      case PaymentMethodOption.STRIPE_PAYPAL:
        return true
      case PaymentMethodOption.VIPPS:
        return false
    }
  })
  const currentStripePaymentMethod = useMemo<
    PaymentAccordionItem | undefined
  >(() => {
    if (!stripeSupported) {
      return undefined
    }
    switch (currentPaymentMethod?.case) {
      case 'card': {
        const cardMask = currentPaymentMethod?.value.cardMask
        const lastFourDigits =
          cardMask.length >= 4 ? cardMask.slice(-4) : cardMask
        return {
          label: t`Use existing`,
          sublabel: t`Card ending in ${lastFourDigits}`,
          icons: [SvgVisa, SvgMastercard],
          method: PaymentMethodOption.STRIPE_CARD,
        }
      }
      case 'paypal': {
        return {
          label: t`Use existing`,
          sublabel: 'PayPal',
          icons: [SvgPaypal],
          method: PaymentMethodOption.STRIPE_PAYPAL,
        }
      }
      default:
        return undefined
    }
  }, [currentPaymentMethod, stripeSupported])

  const currentIsVipps =
    currentPaymentInfo?.paymentInfo?.paymentMethod?.case === 'vippsBilling'

  const [isOpen, setIsOpen] = useState<string>('closed')
  const [items, setItems] = useState<PaymentAccordionItem[]>([])
  const [currentIndex, setCurrentIndex] = useState<number | undefined>()

  useEffect(() => {
    const newItems: PaymentAccordionItem[] = []
    if (currentStripePaymentMethod) {
      newItems.push(currentStripePaymentMethod)
    }
    if (paymentMethods) {
      newItems.push(
        ...paymentMethods.map((pm, index) => {
          switch (pm) {
            case PaymentMethodOption.STRIPE_CARD:
              return {
                label: t`Payment card`,
                icons: [SvgVisa, SvgMastercard],
                method: PaymentMethodOption.STRIPE_CARD,
              }
            case PaymentMethodOption.STRIPE_PAYPAL:
              return {
                label: 'PayPal',
                icons: [SvgPaypal],
                method: PaymentMethodOption.STRIPE_PAYPAL,
              }
            case PaymentMethodOption.VIPPS:
              return {
                label: 'Vipps',
                icons: [SvgVippsText],
                method: PaymentMethodOption.VIPPS,
              }
          }
        }),
      )
    }
    setItems(newItems)

    const nextCurrentIndex =
      currentIndex ||
      (() => {
        const vippsIndex = newItems.findIndex(
          pm => pm.method === PaymentMethodOption.VIPPS,
        )
        const index = currentIsVipps && vippsIndex !== -1 ? vippsIndex : 0
        setCurrentIndex(index)
        return index
      })()
    if (nextCurrentIndex >= newItems.length) {
      setCurrentIndex(0)
    }

    setUseCurrent(Boolean(currentStripePaymentMethod && currentIndex === 0))
    setPaymentOption(newItems.at(nextCurrentIndex)?.method)
  }, [
    currentIndex,
    currentIsVipps,
    currentStripePaymentMethod,
    paymentMethods,
    setPaymentOption,
    setUseCurrent,
  ])

  const toggleOpen = () => {
    setIsOpen('closed')
  }

  return (
    paymentMethods &&
    paymentMethods.length > 1 &&
    currentIndex !== undefined && (
      <Accordion
        type="single"
        collapsible
        value={isOpen}
        onValueChange={setIsOpen}
        className="rounded-md border border-light"
      >
        <AccordionItem value="open" className="border-0">
          <AccordionTrigger className="rounded-md px-2 py-2 pr-[15px] hover:bg-btn-outline-hover hover:no-underline focus:outline-2 focus:outline-sx-primary">
            <div className="grid min-h-[60px] w-full grid-cols-[90px_1fr_auto] items-center gap-4">
              <div className="flex justify-center self-center">
                {items?.at(currentIndex)?.icons.map((icon, index) => (
                  <PlainBrandIcon key={index} icon={icon} height={20} />
                ))}
              </div>
              <div className="flex flex-col text-left">
                <span className="text-lg font-semibold">
                  {items?.at(currentIndex)?.label}
                </span>
                {items?.at(currentIndex)?.sublabel && (
                  <span className="text-sm font-normal">
                    {items?.at(currentIndex)?.sublabel}
                  </span>
                )}
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="py-2">
            <ul>
              {items.map((item, index) => {
                return (
                  <Fragment key={index}>
                    <li>
                      <button
                        type="button"
                        className="grid min-h-[60px] w-full grid-cols-[90px_1fr_auto] items-center gap-4 rounded-md p-2 pr-[15px] hover:bg-btn-outline-hover focus:outline-2 focus:outline-sx-primary"
                        onClick={() => {
                          toggleOpen()
                          setCurrentIndex(index)
                        }}
                      >
                        <div className="flex justify-center self-center">
                          {item.icons.map((icon, index) => (
                            <PlainBrandIcon
                              key={index}
                              icon={icon}
                              height={20}
                            />
                          ))}
                        </div>
                        <div className="flex flex-col text-left">
                          <span className="text-lg font-semibold">
                            {item.label}
                          </span>
                          {item.sublabel && (
                            <span className="text-sm font-normal">
                              {item.sublabel}
                            </span>
                          )}
                        </div>
                        {index === currentIndex && (
                          <PlainBrandIcon
                            height={20}
                            icon={SvgBoldCheck}
                            className="rounded-full bg-good p-1 text-good drop-shadow-sm"
                          />
                        )}
                      </button>
                    </li>
                    {currentStripePaymentMethod && index === 0 && (
                      <div className="flex items-center gap-4 px-4 text-gray-500">
                        <Divider className="w-full" />
                        <p className="whitespace-nowrap">
                          <Trans>or add new</Trans>
                        </p>
                        <Divider className="w-full" />
                      </div>
                    )}
                  </Fragment>
                )
              })}
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    )
  )
}
